<script setup>
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import MetricCard from './overview/MetricCard.vue';
import OverviewEntityList from './overview/OverviewEntityList.vue';
import { LIVE_METRICS, buildLiveMetric } from './overview/liveMetrics';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const { t } = useI18n();
const openOverviewDetail = inject('openOverviewDetail', null);

const store = useStore();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentConversationMetric = useMapGetter('getAgentConversationMetric');
const agents = useMapGetter('agents/getAgents');

const fetchData = () => store.dispatch('fetchAgentConversationMetric');

const { startRefetching } = useLiveRefresh(fetchData);

const items = computed(() =>
  agents.value
    .map(agent => {
      const metric =
        agentConversationMetric.value.find(
          metrics => metrics.assignee_id === Number(agent.id)
        ) || {};
      return {
        id: agent.id,
        name: agent.available_name || agent.name,
        subtitle: agent.email,
        thumbnail: agent.thumbnail,
        status: agent.availability_status,
        metrics: Object.keys(LIVE_METRICS).map(key =>
          buildLiveMetric(t, key, metric[key])
        ),
      };
    })
    .sort(
      (a, b) =>
        b.metrics[0].value - a.metrics[0].value || a.name.localeCompare(b.name)
    )
);

const openAgent = item => {
  openOverviewDetail?.({
    title: item.name,
    description: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER'),
    kind: 'conversations',
    source: 'live',
    params: { metric: 'assigned', assignee_id: item.id },
    tabs: item.metrics.map(metric => ({
      key: metric.key,
      label: metric.label,
      count: metric.value,
    })),
  });
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-row flex-wrap max-w-full">
    <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER')">
      <OverviewEntityList
        :items="items"
        :is-loading="uiFlags.isFetchingAgentConversationMetric"
        :loading-message="
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
        "
        :empty-title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
        :search-placeholder="$t('OVERVIEW_REPORTS.SEARCH_AGENTS')"
        @select="openAgent"
      />
    </MetricCard>
  </div>
</template>
